import type { Brand } from "@src/domain/entities/brand/Brand";

/**
 * Organization Input Interface - Domain layer
 * Defines the input structure for creating an Organization entity.
 * Brands are nested in the Organization entity.
 */
export interface IOrganizationProps {
  organizationId: number;
  organizationKey: string;
  organizationName: string;
  organizationDisplayName: string | null;
  organizationDescription: string | null;
  organizationLogoUrl: string | null;
  organizationIsActive: boolean;
  organizationCreatedAt: string;
  organizationUpdatedAt: string | null;
  organizationDeletedAt: string | null;
  organizationCreatedBy: number;
  organizationUpdatedBy: number | null;
  organizationDeletedBy: number | null;
  brands?: Brand[];
}

/**
 * Organization entity - Domain layer
 * Represents organization data (optionally with its brands) from GraphQL API.
 */
export class Organization {
  private readonly _organizationId: number;
  private readonly _organizationKey: string;
  private readonly _organizationName: string;
  private readonly _organizationDisplayName: string | null;
  private readonly _organizationDescription: string | null;
  private readonly _organizationLogoUrl: string | null;
  private readonly _organizationIsActive: boolean;
  private readonly _organizationCreatedAt: string;
  private readonly _organizationUpdatedAt: string | null;
  private readonly _organizationDeletedAt: string | null;
  private readonly _organizationCreatedBy: number;
  private readonly _organizationUpdatedBy: number | null;
  private readonly _organizationDeletedBy: number | null;
  private readonly _brands: Brand[];

  constructor(props: IOrganizationProps) {
    this._organizationId = props.organizationId;
    this._organizationKey = props.organizationKey;
    this._organizationName = props.organizationName;
    this._organizationDisplayName = props.organizationDisplayName;
    this._organizationDescription = props.organizationDescription;
    this._organizationLogoUrl = props.organizationLogoUrl;
    this._organizationIsActive = props.organizationIsActive;
    this._organizationCreatedAt = props.organizationCreatedAt;
    this._organizationUpdatedAt = props.organizationUpdatedAt;
    this._organizationDeletedAt = props.organizationDeletedAt;
    this._organizationCreatedBy = props.organizationCreatedBy;
    this._organizationUpdatedBy = props.organizationUpdatedBy;
    this._organizationDeletedBy = props.organizationDeletedBy;
    this._brands = props.brands ?? [];
  }

  get organizationId(): number {
    return this._organizationId;
  }

  get organizationKey(): string {
    return this._organizationKey;
  }

  get organizationName(): string {
    return this._organizationName;
  }

  get organizationDisplayName(): string | null {
    return this._organizationDisplayName;
  }

  get organizationDescription(): string | null {
    return this._organizationDescription;
  }

  get organizationLogoUrl(): string | null {
    return this._organizationLogoUrl;
  }

  get organizationIsActive(): boolean {
    return this._organizationIsActive;
  }

  get organizationCreatedAt(): string {
    return this._organizationCreatedAt;
  }

  get organizationUpdatedAt(): string | null {
    return this._organizationUpdatedAt;
  }

  get organizationDeletedAt(): string | null {
    return this._organizationDeletedAt;
  }

  get organizationCreatedBy(): number {
    return this._organizationCreatedBy;
  }

  get organizationUpdatedBy(): number | null {
    return this._organizationUpdatedBy;
  }

  get organizationDeletedBy(): number | null {
    return this._organizationDeletedBy;
  }

  get brands(): Brand[] {
    return this._brands;
  }
}
